import { zodResolver } from "@hookform/resolvers/zod";
import { KdsModal } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { FormProvider, useForm } from "react-hook-form";

import { useOrders, useToasts } from "@/hooks";
import {
  type InferredModalRushOrderSchema,
  modalRushOrderFormSchema,
} from "./ModalRushOrder.schema";
import { type ModalRushOrderProps } from "./ModalRushOrder.types";
import { ModalRushOrderContent } from "./ModalRushOrderContent/ModalRushOrderContent";

export const ModalRushOrder = ({
  isOpen,
  setIsOpen,
  orderItem,
  refecthOrderItems,
}: ModalRushOrderProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();

  const { useRushOrder } = useOrders();
  const { mutateAsync: handleRushOrder } = useRushOrder();

  const methods = useForm<InferredModalRushOrderSchema>({
    defaultValues: {},
    resolver: zodResolver(modalRushOrderFormSchema),
  });

  const handleSuccess = async (data: InferredModalRushOrderSchema) => {
    try {
      await handleRushOrder({
        newDeliveryDate: data?.newDeliveryDate?.toString(),
        orderId: orderItem.order.id,
        orderItemId: orderItem.id,
      });

      add({
        title: data?.newDeliveryDate
          ? t("rushOrderProposal.toast.success.content")
          : t("rushOrderProposal.toast.success.contentNoDate"),
        state: "success",
      });
    } catch (_) {
      add({
        title: t("rushOrderProposal.toast.error.content"),
        state: "error",
      });
    }

    refecthOrderItems?.();
    setIsOpen(false);
  };

  const handleError = () => {
    add({
      title: t("rushOrderProposal.toast.error.content"),
      state: "error",
    });
  };

  return (
    <FormProvider {...methods}>
      <KdsModal
        dialogProps={{
          dialogHeaderProps: {
            title: t("rushOrderProposal.title"),
            description: t("rushOrderProposal.description"),
          },
          content: (
            <ModalRushOrderContent methods={methods} orderItem={orderItem} />
          ),
          dialogFooterProps: {
            buttonProps: [
              {
                children: t("buttons.cancel"),
                variant: "outlined",
                color: "neutral",
                onPress: () => setIsOpen(false),
              },
              {
                children: t("buttons.confirm"),
                onPress: () =>
                  void methods.handleSubmit(handleSuccess, handleError)(),
              },
            ],
          },
        }}
        isOpen={isOpen}
        onOpenChange={() => setIsOpen(false)}
      />
    </FormProvider>
  );
};
